// Gráficas por día del Pulso con fechas (Pau 08-10-2026). SVG propio, como el resto de la Sala.
//   · BarrasDia: una barra por día, la franja gris de «lo normal», la media de 7 días y los días raros en rojo. Al
//     pasar el ratón se marca el día en TODAS las gráficas del rango a la vez (HoverDia) y se puede abrir ese día.
//   · LineaDia: lo mismo en pequeño, como línea (unlock, golden, reply, $ por mensaje).
//   · Tendencia: la línea mínima de una tarjeta o de una fila de la tabla.
//   · BarrasPeriodo: barras sueltas con rótulo (fans distintos por semana), la incompleta rayada.
// Todas enseñan el número exacto al pasar el ratón (Puntero.tsx). Con varias gráficas en un HoverDia, la etiqueta
// completa sale solo en la que tiene el ratón; las demás marcan el mismo día con su línea y su cifra.
import { createContext, useContext, useId, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { diaConSemana, diaCorto } from './fechas';
import { escalaRedonda, numero } from './formato';
import { Etiqueta, FOCO, usePuntero, type FilaEtiqueta } from './Puntero';
import type { BandaDiaSala } from './tipos';
import { useTamano } from './useTamano';

// ── Día marcado con el ratón, compartido por todas las gráficas del rango ──
const Contexto = createContext<{ dia: string | null; marcar: (d: string | null) => void }>({ dia: null, marcar: () => undefined });

export function HoverDia({ children }: { children: ReactNode }) {
  const [dia, marcar] = useState<string | null>(null);
  return <Contexto.Provider value={{ dia, marcar }}>{children}</Contexto.Provider>;
}
export const useHoverDia = () => useContext(Contexto);

/** La hoja de hoy (a medias) al final de BarrasDia: se marca con esta clave, que ninguna otra gráfica tiene. */
const HOY = 'hoy';

/** El día marcado como posición de esta gráfica, para usePuntero. */
function useDiaCompartido(claves: string[]): [number | null, (i: number | null) => void] {
  const { dia, marcar } = useHoverDia();
  const i = dia === null ? -1 : claves.indexOf(dia);
  return [i >= 0 ? i : null, (j) => marcar(j === null ? null : (claves[j] ?? null))];
}

/** Filas comunes de la etiqueta de un día: el valor, la referencia comparada, la media y lo normal. */
function filasDia({
  nombre,
  valor,
  formato,
  marca,
  comparado,
  comparadoTexto,
  media,
  banda,
}: {
  nombre: string;
  valor: number | null;
  formato: (v: number) => string;
  marca: string;
  comparado?: number | null;
  comparadoTexto: string;
  media?: number | null;
  banda?: BandaDiaSala;
}): FilaEtiqueta[] {
  const filas: FilaEtiqueta[] = [{ etiqueta: nombre, valor: valor === null ? t('salaControl.grafica.sinDato') : formato(valor), marca, forma: 'barra' }];
  if (comparado !== null && comparado !== undefined)
    filas.push({ etiqueta: comparadoTexto, valor: formato(comparado), marca: 'border-oficina-texto3', forma: 'discontinua', tenue: true });
  if (media !== null && media !== undefined) filas.push({ etiqueta: t('salaControl.grafica.media7'), valor: formato(media), marca: 'bg-oficina-te-necesita', tenue: true });
  if (banda && banda.bajo !== null && banda.alto !== null)
    filas.push({ etiqueta: t('salaControl.grafica.normal'), valor: `${formato(banda.bajo)} – ${formato(banda.alto)}`, tenue: true });
  return filas;
}

const raroTexto = (b?: BandaDiaSala) => (b?.raro === 1 ? t('salaControl.grafica.raroAlto') : b?.raro === -1 ? t('salaControl.grafica.raroBajo') : null);

/** Rótulos redondos dentro de una escala ajustada a los datos (que no empieza en 0): los múltiplos de un paso redondo
 *  entre `min` y `max` (antes se rotulaban min, medio y max: cifras como 13,7 o 25,4). */
function marcasEnTramo(min: number, max: number): number[] {
  // Tres tramos: con dos, en una línea casi plana salía un solo rótulo y la escala no se leía.
  const paso = escalaRedonda(max - min, 3).marcas[1] ?? max - min;
  const out: number[] = [];
  for (let v = Math.ceil(min / paso - 1e-9) * paso; v <= max + 1e-9; v += paso) out.push(Number(v.toPrecision(12)));
  return out.length ? out : [min, max];
}

/** Tramos de una línea sin unir los huecos (null). */
function tramos(valores: Array<number | null>, x: (i: number) => number, y: (v: number) => number): string[] {
  const res: string[] = [];
  let actual: string[] = [];
  valores.forEach((v, i) => {
    if (v === null) {
      if (actual.length) res.push(actual.join(' '));
      actual = [];
    } else actual.push(`${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  });
  if (actual.length) res.push(actual.join(' '));
  return res;
}

/** Área de la franja de lo normal (solo los días que la tienen). */
function areaBanda(banda: BandaDiaSala[] | undefined, x: (i: number) => number, y: (v: number) => number, min: number): string[] {
  if (!banda) return [];
  const res: string[] = [];
  let arriba: string[] = [];
  let abajo: string[] = [];
  const cerrar = () => {
    if (arriba.length > 1) res.push(`M${arriba.join('L')}L${abajo.reverse().join('L')}Z`);
    arriba = [];
    abajo = [];
  };
  banda.forEach((b, i) => {
    if (b.bajo === null || b.alto === null) return cerrar();
    arriba.push(`${x(i).toFixed(1)},${y(b.alto).toFixed(1)}`);
    abajo.push(`${x(i).toFixed(1)},${y(Math.max(min, b.bajo)).toFixed(1)}`);
  });
  cerrar();
  return res;
}

const media7 = (v: Array<number | null>) =>
  v.map((_, i) => {
    const ventana = v.slice(Math.max(0, i - 6), i + 1).filter((x): x is number => x !== null);
    return i >= 6 && ventana.length >= 5 ? ventana.reduce((s, x) => s + x, 0) / ventana.length : null;
  });

const IZQ = 52;
const DER = 12;
const ARRIBA = 18;
const ABAJO = 22;

export function BarrasDia({
  dias,
  valores,
  banda,
  comparado,
  formato,
  formatoEje = formato,
  etiqueta,
  conMedia = true,
  hoyRayado = null,
  alSeleccionar,
  altoMin = 180,
  serie = '',
}: {
  dias: string[];
  valores: Array<number | null>;
  banda?: BandaDiaSala[];
  /** Línea del periodo comparado, alineada por posición (día 1 con día 1). */
  comparado?: Array<number | null>;
  formato: (v: number) => string;
  formatoEje?: (v: number) => string;
  etiqueta: string;
  conMedia?: boolean;
  /** Valor de hoy (a medias) como barra rayada al final, fuera de la escala de lo normal. */
  hoyRayado?: number | null;
  alSeleccionar?: (dia: string) => void;
  altoMin?: number;
  /** Nombre corto de la cifra en la etiqueta («Ventas»). Sin él, la cifra va sola (el título de la caja ya la nombra). */
  serie?: string;
}) {
  const rayado = useId();
  const [ref, tam] = useTamano<HTMLDivElement>(altoMin);
  const ancho = tam?.ancho ?? 900;
  const alto = tam?.alto ?? 220;
  const conHoy = hoyRayado !== null;
  const n = dias.length + (conHoy ? 1 : 0);
  // La escala la mandan los datos (no la franja de lo normal, que en semanas de crecimiento se abre mucho y
  // aplastaría las barras): la franja se recorta arriba.
  const todos = [...valores, ...(comparado ?? []), hoyRayado].filter((v): v is number => v !== null && v !== undefined);
  const { tope: max, marcas } = escalaRedonda(Math.max(0, ...todos), 3);
  const w = ancho - IZQ - DER;
  const h = alto - ARRIBA - ABAJO;
  const paso = w / Math.max(1, n);
  const x = (i: number) => IZQ + paso * (i + 0.5);
  const y = (v: number) => ARRIBA + h * (1 - Math.min(v, max) / max);
  const anchoBarra = Math.max(1, Math.min(28, paso * 0.72));
  const cadaEtiqueta = Math.max(1, Math.ceil(n / 7));
  const medias = conMedia ? media7(valores) : null;
  const claves = conHoy ? [...dias, HOY] : dias;
  const p = usePuntero({
    n,
    ancho,
    indiceDe: (px) => Math.max(0, Math.min(n - 1, Math.floor((px - IZQ) / paso))),
    externo: useDiaCompartido(claves),
  });
  const iMarcado = p.i ?? -1;
  const esHoy = conHoy && iMarcado === dias.length;
  const valorMarcado = iMarcado < 0 ? null : esHoy ? hoyRayado : (valores[iMarcado] ?? null);
  const abrir = () => {
    if (!alSeleccionar || iMarcado < 0 || esHoy || !p.confirmado()) return;
    alSeleccionar(dias[iMarcado]!);
  };
  return (
    <div ref={ref} className="relative h-full w-full">
      <div {...p.props} className={cn('relative h-full', FOCO, alSeleccionar && 'cursor-pointer')} aria-label={etiqueta} onClick={abrir}
        onKeyDown={(e) => {
          if (e.key === 'Enter') abrir();
          else p.props.onKeyDown(e);
        }}
      >
      <svg viewBox={`0 0 ${ancho} ${alto}`} className={tam ? 'block h-full w-full' : 'block h-auto w-full'} role="img" aria-label={etiqueta}>
        <defs>
          <pattern id={rayado} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="3" height="6" className="fill-oficina-te-necesita" />
          </pattern>
        </defs>
        {iMarcado >= 0 ? <rect x={x(iMarcado) - paso / 2} y={ARRIBA - 4} width={paso} height={h + 4} rx={4} className="fill-oficina-hover" /> : null}
        {marcas.map((v) => (
          <g key={v}>
            <line x1={IZQ} x2={ancho - DER} y1={y(v)} y2={y(v)} className="stroke-oficina-linea" />
            <text x={IZQ - 6} y={y(v) + 4} textAnchor="end" className="fill-oficina-texto3 text-o-mini">
              {formatoEje(v)}
            </text>
          </g>
        ))}
        {areaBanda(banda, x, y, 0).map((d) => (
          <path key={d} d={d} className="fill-oficina-hover" />
        ))}
        {valores.map((v, i) =>
          v === null ? null : (
            <rect
              key={dias[i]}
              x={x(i) - anchoBarra / 2}
              y={y(v)}
              width={anchoBarra}
              height={Math.max(0, y(0) - y(v))}
              rx={Math.min(3, anchoBarra / 3)}
              className={cn(banda?.[i]?.raro ? 'fill-oficina-error' : 'fill-oficina-marca-oscura', iMarcado >= 0 && iMarcado !== i && 'opacity-60')}
            />
          ),
        )}
        {conHoy ? (
          <rect x={x(dias.length) - anchoBarra / 2} y={y(hoyRayado!)} width={anchoBarra} height={Math.max(0, y(0) - y(hoyRayado!))} fill={`url(#${rayado})`} />
        ) : null}
        {comparado
          ? tramos(comparado, x, y).map((pt) => <polyline key={`c${pt}`} points={pt} fill="none" strokeWidth={1.5} strokeDasharray="4 4" className="stroke-oficina-texto3" />)
          : null}
        {medias
          ? tramos(medias, x, y).map((pt) => (
              <polyline key={`m${pt}`} points={pt} fill="none" strokeWidth={2.5} strokeLinejoin="round" className="stroke-oficina-te-necesita" />
            ))
          : null}
        {claves.map((d, i) =>
          i % cadaEtiqueta === 0 && d !== HOY ? (
            <text key={d} x={x(i)} y={alto - 6} textAnchor="middle" className="fill-oficina-texto3 text-o-mini">
              {diaCorto(d)}
            </text>
          ) : null,
        )}
        {iMarcado >= 0 && !p.activa ? (
          // Otra gráfica del rango tiene el ratón: aquí se marca el mismo día con su cifra, sin tapar nada.
          <text x={Math.min(Math.max(x(iMarcado), IZQ + 60), ancho - DER - 60)} y={ARRIBA - 6} textAnchor="middle" className="fill-oficina-texto text-o-nota font-semibold cifras">
            {`${esHoy ? t('salaControl.grafica.hoyAMedias') : diaCorto(dias[iMarcado]!)} · ${valorMarcado === null ? '—' : formato(valorMarcado)}`}
          </text>
        ) : null}
      </svg>
      {iMarcado >= 0 && p.activa ? (
        <Etiqueta
          x={x(iMarcado)}
          ancho={ancho}
          arriba={ARRIBA}
          titulo={esHoy ? t('salaControl.grafica.hoyAMedias') : diaConSemana(dias[iMarcado]!)}
          filas={filasDia({
            nombre: serie,
            valor: valorMarcado,
            formato,
            marca: !esHoy && banda?.[iMarcado]?.raro ? 'bg-oficina-error' : 'bg-oficina-marca-oscura',
            comparado: esHoy ? null : comparado?.[iMarcado],
            comparadoTexto: t('salaControl.grafica.anterior'),
            media: esHoy ? null : medias?.[iMarcado],
            banda: esHoy ? undefined : banda?.[iMarcado],
          })}
          pie={
            esHoy
              ? t('salaControl.grafica.hoyAMediasPie')
              : [raroTexto(banda?.[iMarcado]), alSeleccionar ? t('salaControl.grafica.abrirDia') : null].filter(Boolean).join(' · ') || undefined
          }
        />
      ) : null}
      </div>
    </div>
  );
}

export function LineaDia({
  dias,
  valores,
  banda,
  comparado,
  comparadoTexto = t('salaControl.grafica.anterior'),
  formato,
  formatoEje,
  etiqueta,
  serie = '',
}: {
  dias: string[];
  valores: Array<number | null>;
  banda?: BandaDiaSala[];
  comparado?: Array<number | null>;
  /** Nombre corto de la cifra en la etiqueta; sin él, la cifra va sola. */
  serie?: string;
  /** Qué es la línea discontinua («Periodo anterior», «Objetivo»…). */
  comparadoTexto?: string;
  /** Cifra exacta de la etiqueta (por defecto, la del eje). */
  formato?: (v: number) => string;
  formatoEje: (v: number) => string;
  etiqueta: string;
}) {
  const [ref, tam] = useTamano<HTMLDivElement>(70);
  const ancho = tam?.ancho ?? 320;
  const alto = tam?.alto ?? 100;
  const todos = [...valores, ...(comparado ?? []), ...(banda?.flatMap((b) => [b.alto, b.bajo]) ?? [])].filter((v): v is number => v !== null && v !== undefined);
  const tope = todos.length ? Math.max(...todos) : 1;
  const suelo = todos.length ? Math.min(...todos) : 0;
  const margen = Math.max((tope - suelo) * 0.1, Math.abs(tope) * 0.02, 1e-9);
  const max = tope + margen;
  const min = Math.max(0, suelo - margen);
  const izq = 40;
  const abajo = 18;
  const n = dias.length;
  const x = (i: number) => izq + ((ancho - izq - 6) * (n <= 1 ? 0.5 : i / (n - 1)));
  const y = (v: number) => 6 + (alto - 6 - abajo) * (1 - (v - min) / (max - min));
  const p = usePuntero({
    n,
    ancho,
    indiceDe: (px) => Math.max(0, Math.min(n - 1, Math.round(((px - izq) / (ancho - izq - 6)) * (n - 1)))),
    externo: useDiaCompartido(dias),
  });
  const iMarcado = p.i ?? -1;
  const vMarcado = iMarcado >= 0 ? (valores[iMarcado] ?? null) : null;
  return (
    <div ref={ref} className="relative h-full min-h-[70px] w-full">
      <div {...p.props} className={cn('relative h-full', FOCO)} aria-label={etiqueta}>
      <svg viewBox={`0 0 ${ancho} ${alto}`} className={tam ? 'block h-full w-full' : 'block h-auto w-full'} role="img" aria-label={etiqueta}>
        {marcasEnTramo(min, max).map((v, i) => (
          <g key={i}>
            <line x1={izq} x2={ancho - 6} y1={y(v)} y2={y(v)} className="stroke-oficina-linea" />
            <text x={izq - 5} y={y(v) + 4} textAnchor="end" className="fill-oficina-texto3 text-o-mini">
              {formatoEje(v)}
            </text>
          </g>
        ))}
        {areaBanda(banda, x, y, min).map((d) => (
          <path key={d} d={d} className="fill-oficina-hover" />
        ))}
        {comparado ? tramos(comparado, x, y).map((pt) => <polyline key={`c${pt}`} points={pt} fill="none" strokeWidth={1.2} strokeDasharray="3 3" className="stroke-oficina-texto3" />) : null}
        {iMarcado >= 0 ? <line x1={x(iMarcado)} x2={x(iMarcado)} y1={6} y2={alto - abajo} className="stroke-oficina-texto3" /> : null}
        {tramos(valores, x, y).map((pt) => (
          <polyline key={pt} points={pt} fill="none" strokeWidth={2} strokeLinejoin="round" className="stroke-oficina-te-necesita" />
        ))}
        {valores.map((v, i) => (v !== null && banda?.[i]?.raro ? <circle key={dias[i]} cx={x(i)} cy={y(v)} r={3.5} className="fill-oficina-error" /> : null))}
        {vMarcado !== null ? (
          <circle cx={x(iMarcado)} cy={y(vMarcado)} r={4.5} strokeWidth={2} className={cn(banda?.[iMarcado]?.raro ? 'fill-oficina-error' : 'fill-oficina-te-necesita', 'stroke-oficina-elevada')} />
        ) : null}
        {[0, Math.floor((n - 1) / 2), n - 1].filter((i, k, a) => i >= 0 && a.indexOf(i) === k).map((i) => (
          <text key={i} x={x(i)} y={alto - 4} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} className="fill-oficina-texto3 text-o-mini">
            {dias[i] ? diaCorto(dias[i]!) : ''}
          </text>
        ))}
      </svg>
      {iMarcado >= 0 && p.activa ? (
        <Etiqueta
          x={x(iMarcado)}
          ancho={ancho}
          titulo={diaConSemana(dias[iMarcado]!)}
          filas={filasDia({
            nombre: serie,
            valor: vMarcado,
            formato: formato ?? formatoEje,
            marca: 'bg-oficina-te-necesita',
            comparado: comparado?.[iMarcado],
            comparadoTexto,
            banda: banda?.[iMarcado],
          }).map((f, k) => (k === 0 ? { ...f, forma: 'linea' as const } : f))}
          pie={raroTexto(banda?.[iMarcado]) ?? undefined}
        />
      ) : null}
      </div>
    </div>
  );
}

/** Línea mínima (sin ejes) de una serie: la tendencia de una tarjeta o de una cuenta. Con `rotulos` y `formato`, al
 *  pasar el ratón enseña el punto y su cifra exacta. */
export function Tendencia({
  valores,
  rotulos,
  formato,
  className,
}: {
  valores: Array<number | null>;
  /** Qué es cada punto («8 oct», «semana del 6 oct»). */
  rotulos?: string[];
  formato?: (v: number) => string;
  className?: string;
}) {
  const v = valores.filter((x): x is number => x !== null);
  const max = v.length ? Math.max(...v) : 0;
  const min = v.length ? Math.min(...v) : 0;
  const ancho = 100;
  const alto = 24;
  const n = valores.length;
  const x = (i: number) => (ancho * i) / Math.max(1, n - 1);
  const y = (k: number) => 2 + (alto - 4) * (1 - (max === min ? 0.5 : (k - min) / (max - min)));
  const ultimo = valores.reduce<number>((u, k, i) => (k !== null ? i : u), -1);
  const interactiva = Boolean(rotulos && formato);
  const p = usePuntero({
    n,
    ancho,
    indiceDe: (px) => Math.max(0, Math.min(n - 1, Math.round((px / ancho) * (n - 1)))),
    valido: (i) => valores[i] !== null,
  });
  if (v.length < 2) return null;
  const iM = interactiva && p.i !== null && valores[p.i] !== null ? p.i : null;
  const punto = iM ?? ultimo;
  const svg = (
    <svg viewBox={`0 0 ${ancho} ${alto}`} preserveAspectRatio="none" className="block h-6 w-full overflow-visible" aria-hidden>
      {tramos(valores, x, y).map((pt) => (
        <polyline key={pt} points={pt} fill="none" strokeWidth={1.5} vectorEffect="non-scaling-stroke" className="stroke-oficina-te-necesita" />
      ))}
      {iM !== null ? <line x1={x(iM)} x2={x(iM)} y1={0} y2={alto} strokeWidth={1} vectorEffect="non-scaling-stroke" className="stroke-oficina-texto3" /> : null}
      {punto >= 0 ? <circle cx={x(punto)} cy={y(valores[punto]!)} r={iM !== null ? 3 : 2} vectorEffect="non-scaling-stroke" className="fill-oficina-te-necesita" /> : null}
    </svg>
  );
  if (!interactiva) return <div className={className}>{svg}</div>;
  return (
    <div className={className}>
      <div {...p.props} className={cn('relative', FOCO)} aria-label={rotulos!.map((r, i) => `${r} ${valores[i] === null ? '—' : formato!(valores[i]!)}`).join(', ')}>
        {svg}
        {iM !== null && p.activa ? (
          <Etiqueta x={x(iM)} ancho={ancho} arriba={-8} titulo={rotulos![iM]} filas={[{ etiqueta: '', valor: formato!(valores[iM]!), marca: 'bg-oficina-te-necesita' }]} />
        ) : null}
      </div>
    </div>
  );
}

/** Barras sueltas con rótulo; las incompletas, rayadas («no compares con las demás»). */
export function BarrasPeriodo({
  barras,
  etiqueta,
  formatoEje,
  formato = (v) => numero(v),
}: {
  barras: Array<{ rotulo: string; titulo?: string; valor: number; incompleta: boolean }>;
  etiqueta: string;
  formatoEje: (v: number) => string;
  formato?: (v: number) => string;
}) {
  const rayado = useId();
  const [ref, tam] = useTamano<HTMLDivElement>(120);
  const ancho = tam?.ancho ?? 400;
  const alto = tam?.alto ?? 160;
  const { tope: max, marcas } = escalaRedonda(Math.max(0, ...barras.map((b) => b.valor)), 2);
  const izq = 40;
  const abajo = 20;
  const paso = (ancho - izq - 4) / Math.max(1, barras.length);
  const y = (v: number) => 8 + (alto - 8 - abajo) * (1 - v / max);
  const cada = Math.max(1, Math.ceil(barras.length / 5));
  const p = usePuntero({ n: barras.length, ancho, indiceDe: (px) => Math.max(0, Math.min(barras.length - 1, Math.floor((px - izq) / paso))) });
  const iM = p.i;
  return (
    <div ref={ref} className="relative h-full min-h-[120px] w-full">
      <div {...p.props} className={cn('relative h-full', FOCO)} aria-label={etiqueta}>
      <svg viewBox={`0 0 ${ancho} ${alto}`} className={tam ? 'block h-full w-full' : 'block h-auto w-full'} role="img" aria-label={etiqueta}>
        <defs>
          <pattern id={rayado} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="3" height="6" className="fill-oficina-te-necesita" />
          </pattern>
        </defs>
        {iM !== null ? <rect x={izq + paso * iM} y={4} width={paso} height={alto - abajo - 4} rx={4} className="fill-oficina-hover" /> : null}
        {marcas.map((v) => (
          <g key={v}>
            <line x1={izq} x2={ancho - 4} y1={y(v)} y2={y(v)} className="stroke-oficina-linea" />
            <text x={izq - 5} y={y(v) + 4} textAnchor="end" className="fill-oficina-texto3 text-o-mini">
              {formatoEje(v)}
            </text>
          </g>
        ))}
        {barras.map((b, i) => (
          <g key={b.rotulo}>
            <rect
              x={izq + paso * i + paso * 0.15}
              y={y(b.valor)}
              width={paso * 0.7}
              height={Math.max(0, y(0) - y(b.valor))}
              rx={2}
              {...(b.incompleta ? { fill: `url(#${rayado})` } : { className: cn('fill-oficina-te-necesita', iM !== null && iM !== i && 'opacity-60') })}
            />
            {i % cada === 0 || iM === i ? (
              <text x={izq + paso * (i + 0.5)} y={alto - 5} textAnchor="middle" className={cn('text-o-mini', iM === i ? 'fill-oficina-texto font-semibold' : 'fill-oficina-texto3')}>
                {b.rotulo}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
      {iM !== null && p.activa ? (
        <Etiqueta
          x={izq + paso * (iM + 0.5)}
          ancho={ancho}
          titulo={barras[iM]!.titulo ?? barras[iM]!.rotulo}
          filas={[{ etiqueta: '', valor: formato(barras[iM]!.valor), marca: 'bg-oficina-te-necesita', forma: 'barra' }]}
          pie={barras[iM]!.incompleta ? t('salaControl.grafica.incompleta') : undefined}
        />
      ) : null}
      </div>
    </div>
  );
}
